import React, { useEffect, useRef } from 'react';
// Routing
import { withRouter } from 'react-router-dom';
// Redux
import { actions as appActions } from '~/app/redux/modules/app';
import { actions as overviewActions } from '~/app/redux/modules/overview';
import { actions as cobrandActions } from '~/app/redux/cobrand/duck';
import { actions as defaultsActions } from '~/app/redux/setupPages/defaultsPage/duck';
import { actions as drilldownActions } from '~/app/redux/drilldown/duck';
import { actions as loginSetupActions } from '~/app/redux/loginSetup/duck.js';
import { selectors as loginSelector } from '~/app/redux/loginSetup/selectors';
import { actions as officeProfileActions } from '~/app/redux/officeProfile/duck';
import { actions as returnListActions } from '~/app/redux/returnList/duck';
import { actions as setupPageActions } from '~/app/redux/setupPages/duck';
import { useSelector, useDispatch } from 'react-redux';
// UI
import { Paper, Divider, Button } from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { Line } from 'rc-progress';
// API
import UtilityAPI from '~/app/api/utilityAPI';
import XlinkAPI from '~/app/api/xlinkAPI';
// Helpers
import { checkAppVersion } from '~/app/Pages/Login/loginHelper.js';
import { useSetState } from '~/app/Utility/customHooks';
import { canAccessCorp, isHierarchySuperOrTech } from '~/app/Utility/general.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import WebHelpers, { statusOK } from '~/app/webHelpers.js';
// Constants
import { HIERARCHY_TYPE, CURRENT_SEASON, ROLE_TYPE } from '~/app/constants.js';
// Internal Imports
import AccessControl from '~/app/Components/Auth/AccessControl.jsx';
import CustomerSupportFab from '#/Common/CustomerSupportFab/CustomerSupportFab.jsx';
import MaintenanceRoutineMsg from '#/Common/MaintenanceRoutineMsg/MaintenanceRoutineMsg.jsx';
import Notifications from '~/app/Components/Common/Notifications/Notifications.jsx';
// Local imports
import BusinessFedReturnsSubmitted from './sections/BusinessFedReturnsSubmitted';
import BusinessStateReturnsSubmitted from './sections/BusinessStateReturnsSubmitted';
import CollectedFees from './sections/CollectedFees';
import FedReturnsSubmitted from './sections/FedReturnsSubmitted';
import HeroRow from './sections/HeroRow';
import StateReturnsSubmitted from './sections/StateReturnsSubmitted';
import { setTabs } from './loadTitleTabHelper.js';
// Theme
import dashboardTheme from '~/themes/overviewV2Theme.jsx';

const OverviewV2 = ({ history }) => {
  const payload = WebHelpers.getJWTPayload();
  const dispatch = useDispatch();
  const {
    officeProfile,
    drilldownHistory,
    currentView,
    loginID,
    isFeederOffice,
    cobrand,
    isSearchBarVisible,
    activeAccessLevels,
    showNewSeasonReminder,
    isMaintenanceModeTime,
  } = useSelector(state => ({
    officeProfile: state.officeProfile,
    currentView: state.drilldown.drilldownHistory[state.drilldown.drilldownHistory.length - 1],
    drilldownHistory: state.drilldown.drilldownHistory,
    loginID:
      state.drilldown?.drilldownHistory[state.drilldown?.drilldownHistory?.length - 1]?.loginID,
    isFeederOffice: state.officeProfile.is_feeder_office,
    cobrand: state.cobrand,
    isSearchBarVisible: state.app.isSearchBarVisible,
    activeAccessLevels: loginSelector.getActiveAccessLevels(state),
    showNewSeasonReminder: state.loginSetup.showNewSeasonReminder,
    isMaintenanceModeTime: state.maintenance.isMaintenanceModeTime,
  }));
  const isBusiness = canAccessCorp(payload, officeProfile, currentView, activeAccessLevels);

  const [, setState] = useSetState({
    isAdmin: true,
    snackbar: true,
  });

  const cobrandRef = useRef(cobrand);

  useEffect(() => {
    const isAdmin =
      drilldownHistory.length === 1 &&
      [HIERARCHY_TYPE.ADMIN, HIERARCHY_TYPE.TECH_SUPPORT, HIERARCHY_TYPE.TECH_SUPPORT_SBA].includes(
        currentView?.role,
      );
    setState({
      isAdmin,
    });
  }, [drilldownHistory, currentView]);

  useEffect(() => {
    if (currentView?.role === ROLE_TYPE.PREPARER && !activeAccessLevels?.add_return) {
      history.push({
        pathname: `/tax-returns`,
      });
    }
  }, [activeAccessLevels, currentView?.role]);

  useEffect(() => {
    checkAppVersion();
    dispatch(returnListActions.clearReturnsList());

    // Get user default configuration from setup if an EFIN and below
    if (
      currentView?.role === HIERARCHY_TYPE.PREPARER ||
      currentView?.role === HIERARCHY_TYPE.EFIN
    ) {
      dispatch(defaultsActions.fetchDefaultsConfig(loginID, payload.season));
      dispatch(officeProfileActions.fetchOfficeProfile());
    }

    // Check if the logged in user is an EFIN owner
    if (currentView?.role === HIERARCHY_TYPE.EFIN) {
      XlinkAPI.getEfinOwner()
        .then(res => {
          dispatch(appActions.updateEfinOwnerValue(res.data));
        })
        .catch(error => {
          ErrorHelpers.handleError('Error fetching EFIN owner', error);
        });
    }

    // Only offices and managers (type EFIN) should see the capture signature option
    if (currentView?.role === HIERARCHY_TYPE.EFIN) {
      XlinkAPI.getAccountLinkedToPreparer()
        .then(res => {
          dispatch(appActions.updateLinkToPreparerValue(res.data));
        })
        .catch(error => {
          ErrorHelpers.handleError('Error fetching Login Linked to Preparer', error);
        });
    }

    setBasicInfo();

    // If they login at office level and do not own a license for the latest season, send them a reminder
    // TODO: Once Season 26 is released, remove '&& !(ENVIRONMENT === 'production')` as this is only in place to prevent the popup on Production
    if (showNewSeasonReminder && !(ENVIRONMENT === 'production')) {
      dispatch(
        appActions.showSnackbarMessage(
          `The latest season ${CURRENT_SEASON} is available for purchase`,
          'info',
          3500,
          {
            vertical: 'top',
            horizontal: 'center',
          },
        ),
      );
      dispatch(loginSetupActions.setShowNewSeasonReminder(false));
    }
  }, []);

  useEffect(() => {
    if (currentView?.role) {
      const isEfinPreparer = [HIERARCHY_TYPE.PREPARER, HIERARCHY_TYPE.EFIN].includes(
        currentView?.role,
      );
      setTabs(currentView?.role, dispatch);

      // Obtain access to cobranding
      getHasAccessToAddCobranding();
      // We drilled down so lets grab the drilled down accounts config
      if (
        isEfinPreparer ||
        currentView?.role === HIERARCHY_TYPE.FRANCHISE ||
        currentView?.role === HIERARCHY_TYPE.SSB // Nested reseller
      ) {
        dispatch(defaultsActions.fetchDefaultsConfig(loginID, payload.season));
        dispatch(officeProfileActions.fetchOfficeProfile());
        setBasicInfo();
      }
      // If search is open, ensure we close out of dialog
      if (isSearchBarVisible) {
        dispatch(appActions.toggleSearchBar(false));
      }

      // Fetch office progress setup
      if (canGetSetupProgress(isEfinPreparer, payload?.hierarchy_type_id)) {
        checkSetupProgress();
      }
    }
  }, [currentView]);

  useEffect(() => {
    if (cobrand?.canCobrand !== cobrandRef.current?.canCobrand) {
      cobrandRef.current = cobrand;
    }
  }, [cobrand?.canCobrand]);

  useEffect(() => {
    // Edge case: If the user hasn't received maintenance data yet and it's maintenance time, a page refresh resets the Redux store and the fingerprint logic re-logs them in. We must force logout the user.
    // This scenario only affects active user sessions and occurs if maintenance mode is set immediately without prior notice.
    if (isMaintenanceModeTime) {
      ErrorHelpers.forceLogout('Scheduled Maintenance is currently in progress');
    }
  }, [isMaintenanceModeTime]);

  const getHasAccessToAddCobranding = async () => {
    try {
      const res = await XlinkAPI.getHasAccessToAddCobranding();

      if (statusOK(res)) {
        const { canTogglePurchase, canToggleCobrand, canCobrand, purchasedCobrand } = res.data;

        dispatch(
          cobrandActions.hasAccessToAddCobrandingSet(
            canToggleCobrand,
            canTogglePurchase,
            canCobrand,
            purchasedCobrand,
          ),
        );
      } else {
        ErrorHelpers.handleError('Cobranding Error', 'Unable to retrieve co-branding for account.');
      }
    } catch (error) {
      ErrorHelpers.handleError('Cobranding Error', error);
    }
  };

  // Returns true if Efin is preparer, the top login is not super, and if office has not been skipped via searchbar
  const canGetSetupProgress = (isPreparer, hierarchyID) => {
    return (
      isPreparer &&
      !isHierarchySuperOrTech(hierarchyID) &&
      !(
        [HIERARCHY_TYPE.PREPARER].includes(currentView?.role) &&
        currentView?.loginID !== drilldownHistory[drilldownHistory.length - 2]?.loginID
      )
    );
  };

  const finishSettingUpOffice = () => {
    if (currentView?.role === HIERARCHY_TYPE.EFIN && currentView?.isOfficeSetupComplete === false) {
      return (
        <div className="row ovNotebookTwo">
          <AccessControl
            requiredAction="write"
            accessLevel="view_office_setup"
            alternateView={altWelcomeText}
          >
            <div className="welcome-heading-text">
              Welcome! Please complete the required info for filing.
            </div>
            <div className="welcome-linear-progress-container">
              <Line
                className="welcome-linear-progress-content"
                percent={currentView?.setupProgress}
                strokeWidth="3"
                strokeColor="#00C44F"
              />
            </div>
            <Button
              className="welcome-setup-link"
              color="primary"
              variant="contained"
              onClick={onClickSetup}
            >
              Complete Setup
            </Button>
          </AccessControl>
        </div>
      );
    } else {
      return <></>;
    }
  };

  const setBasicInfo = async () => {
    try {
      const basicInfo = await UtilityAPI.getBasicOfficeInfo(drilldownHistory);
      if (typeof basicInfo !== 'undefined') {
        dispatch(appActions.setLoggedInBasicInfo(basicInfo?.result || basicInfo));
        if (typeof basicInfo?.agreementsStatus !== 'undefined') {
          dispatch(overviewActions.setAgreementsStatus(basicInfo.agreementsStatus));
        }
      }
    } catch (err) {
      ErrorHelpers.handleError('Error setting basic info', err);
      dispatch(
        appActions.showSnackbarMessage('Error setting basic info', 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
    }
  };

  const checkSetupProgress = async () => {
    try {
      const res = await XlinkAPI.getSetupProgressIndicators(isFeederOffice);
      if (statusOK(res)) {
        const data = Object.values(res.data);
        const numTrue = data.reduce((n, setupStep) => n + (setupStep === true), 0);
        const percentage = (numTrue / data.length) * 100;
        if (typeof percentage !== 'undefined') {
          dispatch(drilldownActions.setSetupProgress(percentage));
        }
      }
    } catch (err) {
      ErrorHelpers.handleError('Error retrieving setup progress', err);
      dispatch(
        appActions.showSnackbarMessage('Error retrieving setup progress', 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
    }
  };

  const onClickSetup = () => {
    dispatch(setupPageActions.toggleSetupAccount());
    history.push({
      pathname: `/account-settings`,
      state: {
        fromPreparer: false,
      },
    });
  };

  const altWelcomeText = (
    <div className="welcome-heading-text-centered">
      Welcome! This office hasn&apos;t finished the required setup for filing. Please contact your
      administrator
    </div>
  );

  return (
    <MuiThemeProvider theme={dashboardTheme}>
      <div className="container-fluid" style={{ overflowY: 'auto', height: '100vh' }}>
        <Paper>
          <Divider
            style={{ marginBottom: '3vh', marginTop: '4vh', marginLeft: '4vh', marginRight: '4vh' }}
          ></Divider>
          <MaintenanceRoutineMsg />
          <div className="container-fixed ovContainer">{finishSettingUpOffice()}</div>
          <Notifications />
          <div className="conatiner-fixed ovContainer"></div>
          <CustomerSupportFab cobrand={cobrandRef.current} />
          <HeroRow></HeroRow>
          {activeAccessLevels?.view_collected_fees && <CollectedFees></CollectedFees>}
          <Divider
            style={{ marginBottom: '5vh', marginTop: '1vh', marginLeft: '4vh', marginRight: '4vh' }}
          ></Divider>
          <FedReturnsSubmitted></FedReturnsSubmitted>
          {isBusiness && <BusinessFedReturnsSubmitted></BusinessFedReturnsSubmitted>}
          <Divider
            style={{ marginBottom: '5vh', marginTop: '1vh', marginLeft: '4vh', marginRight: '4vh' }}
          ></Divider>
          <StateReturnsSubmitted></StateReturnsSubmitted>
          {isBusiness && <BusinessStateReturnsSubmitted></BusinessStateReturnsSubmitted>}
          <Divider
            style={{ marginBottom: '5vh', marginTop: '1vh', marginLeft: '4vh', marginRight: '4vh' }}
          ></Divider>
        </Paper>
      </div>
    </MuiThemeProvider>
  );
};

export default withRouter(OverviewV2);
